/**
 * Making room for the module a stop is standing on.
 *
 * 🔴 THIS IS THE SHORT-VIEWPORT HALF OF "THE MODULE AND THE GUIDE BOTH COME OUT
 * WRONG", and it is a different failure from the ones `anchors.ts` already
 * handles. Those are about pointing at the RIGHT element (a shape change swapped
 * the tree, a scroller moved the row). This one is about there not being ENOUGH
 * ROOM for the element that is correctly identified:
 *
 *   - driver.js decides whether a popover can go to the right of a row by WIDTH
 *     ALONE (`innerWidth - rect.right >= popoverWidth`), and never asks whether
 *     it fits vertically. It then clamps the card's top to
 *     `innerHeight - cardHeight`, so on a short screen the guide slides UP away
 *     from the row it is describing — the module is highlighted down here and
 *     the words about it are up there — and once the card is TALLER than the
 *     viewport the clamp bottoms out and its Back/Next row is simply off the
 *     bottom of the screen.
 *   - the stage is drawn with `stagePadding` around the row, so on a list with
 *     no spare vertical rhythm the hole also frames slices of the modules above
 *     and below it. "The modules are not showing correctly in the highlight" is
 *     that: three rows lit up, one of them being described.
 *
 *   - and beside the module is not always a place that EXISTS. driver.js picks
 *     left-or-right on width alone and, when neither has room, drops the card
 *     into an unstyled last-resort position at the bottom of the screen with no
 *     arrow — which is where a phone, a narrow window and an open navigation
 *     drawer all land. The guide ends up over the module it is describing.
 *
 *   - and the room the module has to fit into is NOT the window. This shell's
 *     sidebar is a fixed full-height column whose list scrolls (`flex-1
 *     overflow-auto`) with the Nexa AI workspace entry and the account control
 *     BELOW it as siblings — so the list's visible box ends ~150px short of the
 *     bottom of the screen and anything past that edge is clipped. Measured
 *     against `innerHeight`, the lift given to the modules at the END of the list
 *     (Petpooja, Support) was short by exactly those two footers: the group
 *     opened, the ring was drawn around it, and its last submodule rows sat
 *     behind the footer while nothing scrolled any further, because by the
 *     window's arithmetic the module was already in place. "The submodules do not
 *     auto-scroll into view and hide under the Nexa AI toggle" is that.
 *
 * So this module answers four questions, in the order they have to be answered:
 * WHERE THE GUIDE GOES for the screen it is on (`guideMode` — beside the module
 * where there is room for that, a sheet across the bottom where there is not),
 * HOW MUCH OF THE MODULE CAN BE FRAMED (`groupFits` — a module and its
 * submodules together, or just the module's own row when the group is taller than
 * the space it can be seen in), WHERE THAT SPACE IS (`visibleBand` — the window
 * narrowed by every container that clips between the anchor and the document),
 * and then WHERE THE MODULE HAS TO SIT for that guide to be beside
 * or above it (`fitTop`, applied by `makeRoomFor`) — pushing the neighbouring
 * modules away so the highlight frames one module, and holding space open past
 * the end of the list when the module is near the bottom and the list has nothing
 * left to scroll.
 *
 * 🔴 GEOMETRY ONLY, NO APP KNOWLEDGE. Everything here works off computed styles
 * and rects, so the same code covers the desktop rail, the mobile drawer and a
 * step pointing at something in `<main>`, and nothing in the host has to grow a
 * hook for it. The one host-shaped read is the sidebar's own `menu-item`
 * attribute, which is where "the other modules" live.
 */

import { HOST } from './anchors';

/**
 * Stamped on the menu item the run is standing on. The gap itself lives in
 * `tour-guide.css`, so the visual stays with the rest of the module's styling
 * and a teardown that removes the attribute cannot leave a stray inline style
 * behind on the host's markup.
 */
export const ROOM_ATTR = 'data-tour-room';

/** Stamped on a scroller that is holding space open past its last row. */
export const TAIL_ATTR = 'data-tour-room-tail';

/**
 * Stamped on the guide itself with the placement it is in, so the stylesheet can
 * lay it out for the room it actually has.
 *
 * 🔴 AN ATTRIBUTE RATHER THAN INLINE POSITIONING, because driver.js writes all
 * four offsets (`top`, `right`, `bottom`, `left`) inline on the wrapper on every
 * single placement — anything this module wrote there would be overwritten on the
 * next refresh. A rule in the stylesheet can out-rank an inline style; a second
 * inline style cannot.
 */
export const GUIDE_ATTR = 'data-tour-guide';

/**
 * The custom property carrying that space.
 *
 * Inline because the AMOUNT is computed per step — it is exactly the shortfall
 * between what the scroller can still scroll and what the guide needs — while
 * the rule that consumes it stays in the stylesheet.
 */
export const TAIL_VAR = '--tour-room-tail';

/** Space kept between the guide (or the ring) and the edge of the screen. */
const EDGE = 14;

/**
 * Height to assume for the guide before it has painted once.
 *
 * Only ever used for a first fit: `guideHeight` measures the real card as soon
 * as there is one, and these descriptions vary by well over a hundred pixels
 * (a module's own copy plus its "Includes: …" list plus up to two extra
 * buttons), so a constant on its own would put the band in the wrong place.
 */
export const GUIDE_FALLBACK_HEIGHT = 260;

/**
 * Most space ever held open past the last row.
 *
 * A cap rather than "whatever it takes": the shortfall is bounded by the
 * viewport in practice, and an unbounded value would turn a mis-measurement
 * into a sidebar the user can scroll into empty space forever.
 */
const MAX_TAIL = 520;

/** Below this, a "fix" is noise: it costs a scroll event and moves nothing. */
const EPSILON = 2;

/** The viewport, with the same fallbacks the rest of the module uses. */
function viewportHeight(): number {
  return window.innerHeight || document.documentElement.clientHeight || 0;
}

function viewportWidth(): number {
  return window.innerWidth || document.documentElement.clientWidth || 0;
}

/**
 * Where the guide can go on this screen.
 *
 * `beside` is the placement the tour is written for: the card next to the module,
 * top-aligned with it, pointing at it. `sheet` is the honest answer when there is
 * no room for that — the card spans the bottom of the screen and the module is
 * held above it, which keeps both visible instead of stacking one on the other.
 */
export interface GuideMode {
  mode: 'beside' | 'sheet';
  /** Which side, when there is a side. */
  side?: 'left' | 'right';
}

/**
 * Kept in sync with `.tour-guide-popover { max-width }`.
 *
 * 🔴 A CONSTANT AND NOT A MEASUREMENT, uniquely in this file. Everything else
 * here measures what is on screen, but the card's width is what we are deciding
 * whether there is room FOR — reading it off a card that driver.js has already
 * squeezed into the space it wrongly chose would make the decision depend on its
 * own outcome.
 */
const CARD_WIDTH = 340;

/** Gap between the guide and the thing it is describing. */
const GAP = 14;

/**
 * Narrower than this and a card is not "beside" anything.
 *
 * 🔴 A SECOND TEST, BECAUSE "IT FITS" IS NOT THE SAME QUESTION AS "IT IS BESIDE".
 * The card's width is clamped to the window, so on a 320px phone a card of
 * 292px technically has room next to a zero-width target — and then covers the
 * screen the module is on. Below this, the window itself is too narrow for a
 * side-by-side layout to mean anything, whatever the target is.
 */
const CARD_MIN_BESIDE = 280;

/**
 * Kept inside a clipping container's own edge.
 *
 * A little more than driver.js's `stagePadding` (6), so the ring drawn around a
 * row that has been brought to the bottom of its scroller is not itself cut off
 * by that scroller.
 */
const CLIP_INSET = 8;

/** A strip of screen, in viewport coordinates. */
export interface Band {
  top: number;
  bottom: number;
}

/**
 * The strip of screen an anchor can actually be SEEN in: the viewport, narrowed
 * by every container between the anchor and the document that clips its
 * overflow.
 *
 * 🔴 THIS IS THE "IT IS HIDING UNDER THE NEXA AI TOGGLE" HALF, and it is why
 * every number in this module used to be measured against the wrong bottom edge.
 * This shell's sidebar is a fixed, full-height column: a scrolling list
 * (`[data-sidebar="content"]`, `flex-1 overflow-auto`) with the Nexa AI workspace
 * entry and the account control BELOW it as siblings. So the list's own visible
 * box ends ~150px above the bottom of the window, and content past that edge is
 * clipped — which, at the boundary the footer starts at, looks exactly like a row
 * sliding underneath the toggle.
 *
 * `fitTop` was told the anchor had to clear `innerHeight`, so for the modules at
 * the END of the list — Petpooja, Support — it computed a shift that was short by
 * the whole height of those two footers: the group was opened, the ring was drawn
 * around it, and its last submodule rows were behind the footer with the list
 * never scrolled far enough to bring them out. Measuring the band the anchor
 * lives in, rather than the window it is somewhere inside, is what makes the
 * scroll reach.
 *
 * 🔴 STILL GEOMETRY ONLY. Nothing here names the sidebar, its footer or the
 * toggle: any clipping ancestor counts, so the same arithmetic covers the mobile
 * drawer, a scrolling `<main>` and a host that later grows a third footer.
 */
export function visibleBand(el: HTMLElement): Band {
  const viewport = viewportHeight();
  let top = 0;
  let bottom = viewport;

  for (let node = el.parentElement; node; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    // 🔴 SCROLLERS BY DECLARATION ONLY — the same test `isScrollable` uses, and
    // deliberately NOT the wider one in `isAnchorClipped`. An `overflow: hidden`
    // ancestor that moves WITH the anchor cannot be satisfied by scrolling at
    // all: Radix's accordion panel is one (it clips while it animates open), and
    // counting its box would have the fit chasing a boundary the row is nailed
    // to, scrolling the list for a shortfall no scroll can ever close. The bands
    // worth measuring are the ones this module can actually scroll the anchor
    // within.
    if (!/auto|scroll|overlay/.test(`${style.overflowY}`)) continue;
    const box = node.getBoundingClientRect();
    // An unmeasured ancestor (detached, or mid-mount) would clip everything;
    // skipping it keeps the band honest rather than empty.
    if (box.width === 0 && box.height === 0) continue;
    top = Math.max(top, box.top + CLIP_INSET);
    bottom = Math.min(bottom, box.bottom - CLIP_INSET);
  }

  // A band with nothing in it is not arithmetic anything can be fitted into —
  // a collapsed or offscreen container would otherwise turn every fit below into
  // a scroll to nowhere. The window is the honest fallback.
  if (bottom - top < EPSILON) return { top: 0, bottom: viewport };
  return { top, bottom };
}

/** The whole window, for callers that have no element to measure a band from. */
function wholeViewport(viewport: number): Band {
  return { top: 0, bottom: viewport };
}

/**
 * Pick the placement for a target, from the room actually beside it.
 *
 * 🔴 DECIDED PER STEP, NOT PER BREAKPOINT. The room beside a target depends on
 * the target: on the same 1024px window there is space beside a sidebar row and
 * none beside the header control at the far right, and an open navigation drawer
 * takes ~288px out of the width that a collapsed rail does not. A media query
 * cannot see any of that, which is why the tour's authored `side: 'right'` held
 * on screens where right was not a place a card could be.
 *
 * The card's own width is clamped to the window by CSS, so a window narrower than
 * the card compares against the window — otherwise every step on a phone would
 * be judged against a width that cannot exist there.
 */
export function guideMode(target: { left: number; right: number }): GuideMode {
  const width = viewportWidth();
  const card = Math.min(CARD_WIDTH, Math.max(0, width - EDGE * 2));
  if (card < CARD_MIN_BESIDE) return { mode: 'sheet' };

  const roomRight = width - target.right - GAP - EDGE;
  const roomLeft = target.left - GAP - EDGE;

  if (roomRight >= card) return { mode: 'beside', side: 'right' };
  if (roomLeft >= card) return { mode: 'beside', side: 'left' };
  return { mode: 'sheet' };
}

/**
 * Put the guide into the placement its screen allows.
 *
 * Only the attribute: the geometry of a sheet is CSS's business (see
 * `[data-tour-guide='sheet']`), and driver.js's own arithmetic still runs for the
 * `beside` case, which is the one it is good at once it has been told which side
 * actually has room.
 */
export function dressGuide(card: HTMLElement | null, placement: GuideMode): void {
  card?.setAttribute(GUIDE_ATTR, placement.mode);
}

/**
 * Stamped on the tour's own tracker while the guide is over it.
 *
 * 🔴 THE TRACKER USED TO WIN THIS FIGHT, AND WINNING IT BROKE THE TOUR. Both
 * surfaces are fixed to the bottom-right region, and the tracker sat one z-index
 * above the guide so that it could never be dimmed — so on any stop whose card
 * lands near the right-hand edge, the panel was on top of that card's Back/Next
 * row. The last stop of the tour is one of those, which meant Done could not be
 * pressed: the run never ended, and everything the end of a run restores stayed
 * hidden.
 */
export const COVERED_ATTR = 'data-tour-covered';

/** The guide as it stands: how tall it is, and whether it owns the bottom edge. */
export interface Guide {
  height: number;
  sheet: boolean;
}

/**
 * Is there room to frame a whole group — a module together with the submodules it
 * has just revealed — or only the module's own row?
 *
 * 🔴 THE ANSWER CHANGES WITH THE SCREEN, which is why the module stop asks it
 * every time rather than always ringing the group. An open module in this shell
 * is its trigger row plus up to six submodule rows: comfortably framed on a
 * laptop, taller than the whole window on a phone in landscape — where ringing it
 * means a stage that runs off both the top and the bottom of the screen, no
 * visible boundary anywhere, and a guide with nowhere left to sit. Falling back
 * to the module's own row keeps the highlight meaningful; the submodules are
 * still named in the module's own copy and still get their own stops.
 */
export function groupFits(height: number, guide: Guide, band?: Band): boolean {
  const viewport = viewportHeight();
  // 🔴 THE BAND IS THE OTHER HALF OF THE ANSWER. "Does the group fit" is a
  // question about the space it can be SEEN in, and in this shell that is the
  // sidebar's own scrolling list — which ends above the Nexa AI entry and the
  // account control, not at the bottom of the window. Judged against the window,
  // the last modules' groups passed by ~150px they never had, so a group was
  // framed as one ring whose last rows were behind the footer.
  const top = Math.max(EDGE, band?.top ?? 0);
  // The guide still has the whole window to sit in, so its own limit stays a
  // window measurement; the band only ever narrows the result.
  const forGuide = guide.sheet ? viewport - guide.height - GAP - EDGE : viewport - EDGE;
  const bottom = Math.min(band?.bottom ?? viewport, forGuide);
  return height > 0 && height <= bottom - top;
}

/**
 * The guide currently on screen, or null when there is none.
 *
 * 🔴 ONE PLACE KNOWS DRIVER.JS'S SELECTOR. The card is built by driver.js and
 * then added to by this module (the task invitation, the module skip), so neither
 * side can be told its size — it has to be found and measured. The engine needs
 * the same node on its resize path, where no hook hands it one.
 */
export function liveGuide(): HTMLElement | null {
  return document.querySelector<HTMLElement>('.driver-popover');
}

/**
 * The height of the guide currently on screen, or the fallback when there is
 * none yet.
 */
export function guideHeight(): number {
  const measured = liveGuide()?.getBoundingClientRect().height ?? 0;
  return measured > 0 ? measured : GUIDE_FALLBACK_HEIGHT;
}

/**
 * Where the anchor's top edge has to be for this guide to be seen next to it.
 *
 * Pure, and exported for that reason: this is the arithmetic the whole fix turns
 * on, and it is the one part that can be checked without a layout.
 *
 * BESIDE: the card is top-aligned with the anchor (`align: 'start'` in
 * `driveStep`), so it occupies `[top, top + height]`. Keeping that on screen puts
 * the top at `viewport - EDGE - height` or above.
 *
 * 🔴 WHEN THE CARD CANNOT FIT BESIDE AT ALL the answer is the TOP of the screen,
 * not the middle. A card taller than the window is clamped by CSS (`max-height`
 * on `.tour-guide-popover`) and then spans the full height whatever the anchor
 * does, so the only thing left to decide is whether the highlighted module is
 * visible — and top-aligning it matches where the card starts.
 *
 * SHEET: the card owns the bottom of the screen, so the whole anchor has to be
 * ABOVE it — the bottom edge of the module, not just its top, is what has to
 * clear the sheet. 🔴 THIS IS THE PART A "SCROLL IT INTO VIEW" CANNOT DO: a row
 * that is perfectly in view is still invisible when the guide is sitting on top
 * of it, which is what a phone-width run looked like.
 *
 * Both clamp to `EDGE` at the top, so an anchor taller than the room it has shows
 * from its top edge down rather than being centred on a boundary nobody can see.
 */
export function fitTop(
  anchor: { top: number; height: number },
  guide: Guide,
  viewport: number,
  band: Band = wholeViewport(viewport),
): number {
  // 🔴 THE ANCHOR HAS TO CLEAR THE BOTTOM OF ITS OWN BAND, NOT OF THE WINDOW.
  // This is the reported failure: a module at the end of the sidebar's list was
  // lifted only until it cleared `innerHeight`, and the list's visible box stops
  // ~150px short of that — the Nexa AI entry and the account control sit below it
  // as siblings, not on top of it. So the shift was short by exactly the height
  // of those footers, the group's last submodule rows stayed clipped behind them,
  // and no further scrolling happened because by the window's arithmetic the
  // module was already where it needed to be. See `visibleBand`.
  const bandTop = Math.max(EDGE, band.top);
  const bandBottom = Math.min(band.bottom, viewport - EDGE);

  // 🔴 THE ANCHOR'S OWN HEIGHT COUNTS IN BOTH MODES, and leaving it out of the
  // `beside` case is what left a module's submodules below the fold. The card is
  // top-aligned, so constraining only the anchor's TOP is enough for the card —
  // and says nothing about the anchor's BOTTOM. A module ringed as its whole
  // accordion item is 100–250px tall, so a group near the end of the list passed
  // the guide's test with its last submodules off the screen: highlighted,
  // described, and not visible. Measured at 1024×560 on Ads, Petpooja and
  // Support; at 1280×620 on Support.
  const lowestForGuide = guide.sheet
    ? viewport - guide.height - GAP
    : viewport - EDGE - Math.min(guide.height, Math.max(0, viewport - EDGE * 2));
  const lowestForAnchor = bandBottom - anchor.height;

  // Whichever is more restrictive: the guide has to fit beside it AND the thing
  // being described has to be visible. An anchor taller than the room it has
  // makes the second term negative, and the clamp then shows it from its top edge
  // down — which is all that can be done, and is what `groupFits` is for.
  const lowest = guide.sheet
    ? Math.min(lowestForGuide - anchor.height, lowestForAnchor)
    : Math.min(lowestForGuide, lowestForAnchor);

  // Clamped to the TOP of the band for the same reason: pushing a row up past the
  // top edge of the list it lives in hides it just as completely as the footer
  // does, only at the other end.
  return Math.min(Math.max(anchor.top, bandTop), Math.max(bandTop, lowest));
}

/**
 * Can this element be scrolled to move its content — now, or once it has been
 * given room to scroll into?
 *
 * 🔴 `mayGrow` IS NOT A CONVENIENCE. A list whose rows all fit has nothing to
 * scroll and is rejected by the plain test — and that is exactly the sidebar of a
 * phone-sized window, where the guide is a sheet across the bottom and the module
 * it describes is behind it. The row cannot be lifted by scrolling a list that
 * has no overflow; it can be lifted once the list is holding space open past its
 * last row (see `scrollContainer`), which is only worth attempting on a container
 * that is a SCROLLER by declaration. A `visible`-overflow ancestor can never be
 * made to scroll, whatever room it is given.
 */
function isScrollable(el: HTMLElement, mayGrow: boolean): boolean {
  const style = window.getComputedStyle(el);
  if (!/auto|scroll|overlay/.test(`${style.overflowY}`)) return false;
  return mayGrow || el.scrollHeight - el.clientHeight > EPSILON;
}

/**
 * Give the module the run is standing on room of its own.
 *
 * 🔴 THE GAP GOES ON THE MENU ITEM, NOT ON THE HIGHLIGHTED ROW. A module with
 * submodules is highlighted as its whole accordion item once the group is open
 * (see `RunEntry.scopeLabels`), and a submodule stop is highlighted as one row
 * INSIDE that item — in both cases "the other modules" are the item's siblings,
 * so the item is what has to push them away. Spacing the row itself would open a
 * gap between a module and its own submodules instead.
 *
 * 🔴 SIDEBAR ONLY. A step can point at anything — a header button, a card in
 * `<main>` — and margin on one of those moves host chrome for no benefit: those
 * targets have room around them already, and the band scroll below is what they
 * actually need. So a target with no menu item above it gets no gap.
 */
function spaceNeighbours(el: HTMLElement): void {
  el.closest<HTMLElement>(HOST.sidebarMenuItem)?.setAttribute(ROOM_ATTR, '');
}

/**
 * Scroll one container by `amount`, growing its scrollable range when it has
 * run out, and report how far the content actually moved.
 *
 * 🔴 GROWING IT IS THE POINT, and it is what nothing else in this module could
 * do. The last modules in the list cannot be brought up into the band by
 * scrolling, because the list is already at its end — `scrollTop` simply
 * refuses. Holding space open past the last row is what gives them somewhere to
 * move to, which is the "move the other modules down so the current one has
 * space" the report asks for, applied to the only direction that is left.
 *
 * Reading `scrollTop` back rather than trusting the assignment is deliberate:
 * the browser clamps it silently, and the difference between what was asked and
 * what happened is exactly the shortfall the tail has to cover.
 */
function scrollContainer(el: HTMLElement, amount: number, mayGrow: boolean): number {
  const before = el.scrollTop;
  el.scrollTop = before + amount;
  const moved = el.scrollTop - before;

  const short = amount - moved;
  // Only ever grown at the END, so only a downward shortfall (the module is
  // below the band and has to come up) can be answered this way.
  if (!mayGrow || short <= EPSILON) return moved;

  const base = Number.parseFloat(window.getComputedStyle(el).paddingBottom) || 0;
  const tail = Math.min(MAX_TAIL, Math.ceil(short));
  el.style.setProperty(TAIL_VAR, `${base + tail}px`);
  el.setAttribute(TAIL_ATTR, '');

  // Re-read: the padding above changed `scrollHeight`, so the range this second
  // assignment is clamped against is not the one the first was.
  const grown = el.scrollTop;
  el.scrollTop = grown + short;
  return moved + (el.scrollTop - grown);
}

/**
 * Move the anchor `shift` pixels higher up the screen (negative to move it
 * down), through whatever combination of scrollers it takes.
 *
 * Walks OUTWARD from the element: the sidebar's own list first, then `<main>`,
 * then the page. That order matters on a short viewport, where both the list and
 * the document can scroll — moving the list is local and leaves the rest of the
 * screen alone, so the page is only asked for what the list could not give.
 */
function shiftIntoBand(el: HTMLElement, shift: number): void {
  let remaining = shift;

  for (let node = el.parentElement; node && Math.abs(remaining) > EPSILON; node = node.parentElement) {
    if (!isScrollable(node, shift > 0)) continue;
    remaining -= scrollContainer(node, remaining, shift > 0);
  }

  if (Math.abs(remaining) > EPSILON) {
    const before = window.scrollY;
    try {
      window.scrollBy({ top: remaining, behavior: 'auto' });
    } catch {
      // jsdom, and older engines that reject the options object. The page-level
      // scroll is the last resort anyway; a container above usually took it.
      window.scrollBy?.(0, remaining);
    }
    remaining -= window.scrollY - before;
  }
}

/**
 * Give the highlighted module the space it needs.
 *
 * 🔴 THE CALLER STILL HAS TO HAND THE LAYOUT BACK TO DRIVER.JS. This scrolls and
 * it reflows, and driver.js goes on drawing the stage where the row used to be
 * until something re-measures — which is why the engine calls this either
 * immediately before driver's own placement (`onPopoverRender`) or immediately
 * before a `refresh()` (the resize path), and never on its own.
 *
 * 🔴 RELEASES THE PREVIOUS STEP'S ROOM FIRST. Room is granted to ONE module at a
 * time: a gap left behind on every module the tour has visited would walk the
 * whole list downwards over a run, and tail space left on the scroller would
 * leave the sidebar scrollable into emptiness long after the step that needed it.
 *
 * 🔴 MEASURED AFTER THE GAP IS STAMPED, in that order and not the other way
 * round. The gap changes the row's own top (it takes margin above as well as
 * below), so a rect read before it is a rect of a layout that no longer exists —
 * and the band would be computed for the wrong position by exactly the gap.
 */
export function makeRoomFor(el: HTMLElement, guide: Guide): void {
  releaseRoom();

  spaceNeighbours(el);

  const rect = el.getBoundingClientRect();
  // A detached or unrendered node has no position to reason about; the engine's
  // own re-resolve path is what handles that, not this.
  if (rect.width === 0 && rect.height === 0) return;

  // 🔴 THE BAND IS MEASURED FROM THE ELEMENT, not assumed to be the window. For
  // a sidebar row that is the list's own visible box, which stops above the Nexa
  // AI entry; for a header control or a card in `<main>` there is no clipping
  // ancestor and it IS the window, so nothing changes for those.
  const shift = rect.top - fitTop(rect, guide, viewportHeight(), visibleBand(el));
  if (Math.abs(shift) <= EPSILON) return;

  shiftIntoBand(el, shift);
}

/**
 * Keep the tracker out of the guide's way, or put it back when it is not.
 *
 * Called after every placement — from the settle loop, which runs once the guide
 * has actually been positioned, since where driver.js puts the card is not known
 * until after it has put it there.
 *
 * The stacking order in `tour-guide.css` already guarantees the guide is on top
 * and therefore usable; this is what keeps it READABLE, and keeps a
 * half-obscured panel from looking like a broken one.
 */
export function keepTrackerClear(): void {
  const tracker = document.querySelector<HTMLElement>('.tour-guide-tracker');
  if (!tracker) return;

  const guide = liveGuide();
  if (!guide) {
    tracker.removeAttribute(COVERED_ATTR);
    return;
  }

  // Measured with the tracker VISIBLE or not: `visibility: hidden` keeps the box,
  // which is exactly why it is the property used — the overlap test stays true
  // while it is hidden, so the panel does not flicker back and forth.
  const card = guide.getBoundingClientRect();
  const panel = tracker.getBoundingClientRect();
  const overlaps =
    card.left < panel.right &&
    card.right > panel.left &&
    card.top < panel.bottom &&
    card.bottom > panel.top;

  if (overlaps) tracker.setAttribute(COVERED_ATTR, '');
  else tracker.removeAttribute(COVERED_ATTR);
}

/**
 * Put every layout change this module made back.
 *
 * Found by attribute rather than remembered by reference: these are React's
 * nodes, and a row that was re-rendered between the stamp and the release is a
 * different node with the same job. Safe to call at any time, and safe to call
 * twice.
 */
export function releaseRoom(): void {
  document.querySelectorAll<HTMLElement>(`[${ROOM_ATTR}]`).forEach((el) => {
    el.removeAttribute(ROOM_ATTR);
  });
  document.querySelectorAll<HTMLElement>(`[${COVERED_ATTR}]`).forEach((el) => {
    el.removeAttribute(COVERED_ATTR);
  });
  document.querySelectorAll<HTMLElement>(`[${TAIL_ATTR}]`).forEach((el) => {
    el.removeAttribute(TAIL_ATTR);
    el.style.removeProperty(TAIL_VAR);
  });
}
